import { useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/shadcn/card.tsx';
import { Input } from '@/components/shadcn/input.tsx';
import { Label } from '@/components/shadcn/label.tsx';
import { Textarea } from '@/components/shadcn/textarea.tsx';
import type { ValidationChange } from '@/modules/governmentIntake/base-entity.tsx';
import HeaderAndSub from '@/modules/governmentIntake/components/shared-component.tsx';
import {
  EMPTY_PROJECTS_PAIN_POINTS,
  type ProjectsPainPointsData,
  useIntakeForm,
} from '@/modules/governmentIntake/context/intake-form-context.tsx';

export default function ProjectsSubPainPoints({ onValidationChange }: ValidationChange) {
  const { data, updateProjectsSubSection } = useIntakeForm();
  const painPointsData = data.projects?.painPoints ?? EMPTY_PROJECTS_PAIN_POINTS;

  const onValidationChangeRef = useRef(onValidationChange);
  onValidationChangeRef.current = onValidationChange;

  useEffect(() => {
    onValidationChangeRef.current?.(true);
  }, []);

  const updatePainPoints = (newData: ProjectsPainPointsData) => {
    updateProjectsSubSection('painPoints', newData);
  };

  const handleFieldChange = <K extends keyof ProjectsPainPointsData>(field: K, value: ProjectsPainPointsData[K]) => {
    updatePainPoints({
      ...painPointsData,
      [field]: value,
    });
  };

  return (
    <div className='flex w-full flex-col overflow-y-auto'>
      <header className='shrink-0 pt-4'>
        <HeaderAndSub title='Needs & Pain Points' subTitle='Identify operational challenges and define what success looks like.' />
      </header>

      <section className='flex-1 overflow-y-auto pb-4'>
        <div className='pt-8'>
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            {/* Left Column - Operational Friction */}
            <Card className='shadow-none py-4 border-2 border-gray-300 max-w-xl'>
              <CardContent className='py-2'>
                <HeaderAndSub
                  title='Operational Friction'
                  titleClass='text-lg'
                  subTitle='Where does the current process slow you down?'
                  subtitleClass='text-gray-500 pt-1'
                />
                <div className='space-y-10 pt-6'>
                  {/* Biggest administrative burden */}
                  <div className='space-y-2 pt-4'>
                    <Label className='block text-base font-medium text-black'>Departments involved in projects</Label>
                    <Input
                      className='w-full resize-none bg-gray-50 shadow-none border-gray-300'
                      placeholder='eg., Planning, Public Works, Finance, IT'
                      value={painPointsData.deptInvolved}
                      onChange={(e) => handleFieldChange('deptInvolved', e.target.value)}
                    />
                  </div>

                  {/* Where delays or errors occur */}
                  <div className='space-y-2'>
                    <Label className='block text-base font-medium text-black'>External partners involved</Label>
                    <Input
                      className='w-full resize-none bg-gray-50 shadow-none border-gray-300'
                      placeholder='eg., Contractors, consultants, county agencies'
                      value={painPointsData.externalPartnersInvolved}
                      onChange={(e) => handleFieldChange('externalPartnersInvolved', e.target.value)}
                    />
                  </div>

                  {/* What leadership asks for */}
                  <div className='space-y-2'>
                    <Label className='block text-base font-medium text-black'>Visibility gaps across departments</Label>
                    <Textarea
                      rows={4}
                      className='w-full resize-none bg-gray-50 shadow-none border-gray-300'
                      placeholder="Reports, metrics, or information that's difficult to generate quickly..."
                      value={painPointsData.visibilityGaps}
                      onChange={(e) => handleFieldChange('visibilityGaps', e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Right Column - Priority & Outcomes */}
            <Card className='shadow-none py-4 border-2 border-gray-300'>
              <CardContent className='py-2'>
                <HeaderAndSub
                  title='Priority & Outcomes'
                  titleClass='text-lg'
                  subTitle='What matters most and what does success look like?'
                  subtitleClass='text-gray-500 pt-1'
                />
                <div className='pt-8'>
                  {/* Top 3 pain points */}
                  <Label className='block text-base font-medium text-black mb-2'>Top 3 pain points (ranked)</Label>

                  <div className='space-y-4'>
                    <div className='space-y-1'>
                      <Label className='block text-base font-medium text-black'>1. Highest priority pain point</Label>
                      <Input
                        type='text'
                        className='w-full resize-none bg-gray-50 shadow-none border-gray-300'
                        placeholder='e.g., Manual data entry for participant applications'
                        value={painPointsData.painPoint1}
                        onChange={(e) => handleFieldChange('painPoint1', e.target.value)}
                      />
                    </div>

                    <div className='space-y-1'>
                      <Label className='block text-base font-medium text-black'>2. Second priority pain point</Label>
                      <Input
                        type='text'
                        className='w-full resize-none bg-gray-50 shadow-none border-gray-300'
                        placeholder='e.g., Difficult to track program compliance'
                        value={painPointsData.painPoint2}
                        onChange={(e) => handleFieldChange('painPoint2', e.target.value)}
                      />
                    </div>

                    <div className='space-y-1'>
                      <Label className='block text-base font-medium text-black'>3. Third priority pain point</Label>
                      <Input
                        type='text'
                        className='w-full resize-none bg-gray-50 shadow-none border-gray-300'
                        placeholder='e.g., Reporting to funders is time-consuming'
                        value={painPointsData.painPoint3}
                        onChange={(e) => handleFieldChange('painPoint3', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Success in 6-12 months */}
                  <div className='space-y-2 pt-10'>
                    <Label className='block text-base font-medium text-black'>What success looks like in 6-12 months</Label>
                    <Textarea
                      rows={4}
                      className='w-full resize-none bg-gray-50 shadow-none border-gray-300'
                      placeholder='Describe measurable outcomes or improvements that would indicate success...'
                      value={painPointsData.successIn6To12Months}
                      onChange={(e) => handleFieldChange('successIn6To12Months', e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
